// src/pages/admin/AdminLessonWeek.tsx
//
// One curriculum week in the lesson library (/admin/curriculum/:weekKey).
//
//   Micro-lessons   — each with its full script (what is shown / what is
//                     said), activity, evidence and video. Admins can edit
//                     the script, print it for recording, and upload,
//                     replace, take down or restore the lesson video.
//   Weekly target   — what the child must have done by the end of the week,
//                     the parent brief and the home materials.
//   Lesson plan     — the full original lesson plan the micro-lessons were
//                     made from, so any recording stays in line with it.

import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  EVIDENCE_TYPES,
  RUBRIC_AREAS,
  STRANDS,
  useAddMicroLessonMutation,
  useAttachVideoMutation,
  useDeleteMicroLessonMutation,
  useGetLibraryWeekQuery,
  useGetVideoUploadUrlMutation,
  useGetVideoUrlMutation,
  useRemoveVideoMutation,
  useRestoreVideoMutation,
  useUpdateLibraryWeekMutation,
  useUpdateMicroLessonMutation,
} from "../../app/api/libraryApi";
import type {
  LessonPatch,
  LessonVideo,
  MicroLesson,
  ScriptScene,
  WeekDetail,
} from "../../app/api/libraryApi";
import { Button, EmptyState, Skeleton, Spinner, trackConfig } from "./AdminShared";
import MediaPlayer from "../../components/MediaPlayer";
import { CONVERSION_LABEL } from "./AdminCurriculum";

import { ArrowDown, ArrowLeft, ArrowUp, Clapperboard, FileText, Play, Square, Star } from "lucide-react";
/* ─────────────────────────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────────────────────────── */

const SPOKEN_WPM = 130; // children's narration pace

function errorText(err: unknown, fallback: string) {
  const e = err as { data?: { error?: string }; message?: string };
  return e?.data?.error ?? e?.message ?? fallback;
}

function fmtDuration(sec: number | null | undefined) {
  if (sec == null) return "—";
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

function fmtSize(bytes: number | null | undefined) {
  if (!bytes) return "—";
  const mb = bytes / (1024 * 1024);
  return mb >= 1024 ? `${(mb / 1024).toFixed(2)} GB` : `${mb.toFixed(1)} MB`;
}

function fmtWhen(iso: string | null | undefined) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const escapeHtml = (s: string) =>
  s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

/** A printable recording script for one or all micro-lessons of a week. */
function printScript(week: WeekDetail, lessons: MicroLesson[]) {
  const body = lessons
    .map(
      (l) => `
      <section>
        <h2>${l.order}. ${escapeHtml(l.title)}</h2>
        <p class="meta">${escapeHtml(l.strand)} · Watch ${l.watchMinutes} min · Do ${l.doMinutes} min · Upload ${l.uploadMinutes} min · ${l.narrationWords} words (≈ ${Math.max(1, Math.round(l.narrationWords / SPOKEN_WPM))} min spoken)</p>
        ${l.objective ? `<p class="obj"><b>Objective:</b> ${escapeHtml(l.objective)}</p>` : ""}
        <table>
          <thead><tr><th>#</th><th>On screen</th><th>Say this</th></tr></thead>
          <tbody>
            ${l.script
              .map(
                (s, i) =>
                  `<tr><td>${i + 1}</td><td class="screen">${escapeHtml(s.screen)}</td><td>${escapeHtml(s.narration)}</td></tr>`,
              )
              .join("")}
          </tbody>
        </table>
        <h3>After the video, the child…</h3>
        <ol>${l.action.steps.map((s) => `<li>${escapeHtml(s)}</li>`).join("")}</ol>
        ${l.evidence.prompt ? `<p><b>Evidence:</b> ${escapeHtml(l.evidence.prompt)}</p>` : ""}
      </section>`,
    )
    .join("");

  const html = `<!doctype html><html><head><meta charset="utf-8">
    <title>${escapeHtml(week.key)} — recording script</title>
    <style>
      body{font-family:Georgia,serif;color:#222;max-width:900px;margin:32px auto;padding:0 24px;line-height:1.5}
      h1{font-size:22px;margin:0} .sub{color:#666;margin:4px 0 24px}
      section{page-break-after:always;margin-bottom:40px}
      h2{font-size:18px;margin:0 0 4px} .meta{color:#666;font-size:12px;margin:0 0 8px}
      table{border-collapse:collapse;width:100%;font-size:13px;margin:12px 0}
      th,td{border:1px solid #ccc;padding:8px;vertical-align:top;text-align:left}
      th{background:#f3f3f3} td:first-child{width:24px;color:#888}
      td.screen{width:32%;color:#555;font-style:italic;font-family:Arial,sans-serif;font-size:12px}
      h3{font-size:14px;margin:12px 0 4px}
    </style></head><body>
    <h1>${escapeHtml(week.title)}</h1>
    <p class="sub">${escapeHtml(week.key)} · Week ${week.weekNumber} · ${escapeHtml(week.term)} · Recording script</p>
    ${body}
    </body></html>`;

  const win = window.open("", "_blank");
  if (!win) return;
  win.document.open();
  win.document.write(html);
  win.document.close();
  win.focus();
  win.print();
}

function scriptAsText(l: MicroLesson) {
  return [
    `${l.order}. ${l.title}`,
    "",
    ...l.script.map(
      (s, i) => `[${i + 1}] ON SCREEN: ${s.screen}\nSAY: ${s.narration}\n`,
    ),
  ].join("\n");
}

/** Read a video file's duration in the browser before uploading it. */
function readDuration(file: File): Promise<number | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const v = document.createElement("video");
    v.preload = "metadata";
    v.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      resolve(Number.isFinite(v.duration) ? v.duration : null);
    };
    v.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    v.src = url;
  });
}

/** PUT a file straight to R2, reporting progress 0–100. */
function putWithProgress(
  url: string,
  file: File,
  onProgress: (pct: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    xhr.setRequestHeader("Content-Type", file.type);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error(`Upload failed (${xhr.status})`));
    xhr.onerror = () =>
      reject(new Error("Upload failed — check your connection and the storage CORS settings"));
    xhr.send(file);
  });
}

const inputCls =
  "w-full rounded-xl border border-[#dde2ec] bg-white px-3 py-2 text-xs text-[#16213e] outline-none focus:border-[#1b2d5e]";
const labelCls = "mb-1 block text-[11.5px] font-bold uppercase tracking-wider text-[#8189a0]";

/* ─────────────────────────────────────────────────────────────────
   SCRIPT
───────────────────────────────────────────────────────────────── */

function ScriptPanel({ lesson, week }: { lesson: MicroLesson; week: WeekDetail }) {
  const [editing, setEditing] = useState(false);
  const [scenes, setScenes] = useState<ScriptScene[]>(lesson.script);
  const [msg, setMsg] = useState("");
  const [save, { isLoading }] = useUpdateMicroLessonMutation();

  useEffect(() => setScenes(lesson.script), [lesson.script]);

  const words = scenes.reduce(
    (n, s) => n + s.narration.split(/\s+/).filter(Boolean).length,
    0,
  );
  const spokenMin = words / SPOKEN_WPM;
  const overWatch = spokenMin > lesson.watchMinutes + 0.5;

  const setScene = (i: number, patch: Partial<ScriptScene>) =>
    setScenes((s) => s.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const move = (i: number, d: -1 | 1) =>
    setScenes((s) => {
      const j = i + d;
      if (j < 0 || j >= s.length) return s;
      const copy = [...s];
      [copy[i], copy[j]] = [copy[j], copy[i]];
      return copy;
    });

  const onSave = async () => {
    setMsg("");
    try {
      await save({ key: lesson.key, weekKey: lesson.weekKey, body: { script: scenes } }).unwrap();
      setEditing(false);
      setMsg("Script saved");
    } catch (err) {
      setMsg(errorText(err, "Could not save the script"));
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(scriptAsText(lesson));
      setMsg("Script copied");
    } catch {
      setMsg("Copy failed — use Print instead");
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className={`text-[12.5px] ${overWatch ? "font-semibold text-[#b94a3a]" : "text-[#8189a0]"}`}>
          {scenes.length} scenes · {words} words · about {spokenMin.toFixed(1)} min spoken
          {overWatch && ` — longer than the ${lesson.watchMinutes}-minute watch time`}
        </p>
        <div className="flex gap-2">
          {!editing ? (
            <>
              <Button size="sm" variant="secondary" onClick={copy}>Copy</Button>
              <Button size="sm" variant="secondary" onClick={() => printScript(week, [lesson])}>
                Print script
              </Button>
              <Button size="sm" onClick={() => setEditing(true)}>Edit script</Button>
            </>
          ) : (
            <>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  setScenes(lesson.script);
                  setEditing(false);
                }}
              >
                Cancel
              </Button>
              <Button size="sm" onClick={onSave} disabled={isLoading}>
                {isLoading ? "Saving…" : "Save script"}
              </Button>
            </>
          )}
        </div>
      </div>
      {msg && <p className="text-[12.5px] font-semibold text-[#5a6278]">{msg}</p>}

      {scenes.length === 0 && !editing && (
        <p className="rounded-[14px] border border-dashed border-[#cdd4e2] px-4 py-6 text-center text-xs text-[#8189a0]">
          No script yet. Click “Edit script” to write one.
        </p>
      )}

      <div className="overflow-hidden rounded-[14px] border border-[#e5e8ef]">
        {scenes.map((s, i) => (
          <div
            key={i}
            className="grid gap-3 border-b border-[#ebeff7] px-4 py-3 last:border-0 sm:grid-cols-[28px_1fr_2fr]"
          >
            <span className="text-[12.5px] font-bold text-[#a6adbf]">{i + 1}</span>
            {editing ? (
              <>
                <textarea
                  className={`${inputCls} min-h-[70px] italic`}
                  value={s.screen}
                  placeholder="What is shown on screen"
                  onChange={(e) => setScene(i, { screen: e.target.value })}
                />
                <div className="space-y-1.5">
                  <textarea
                    className={`${inputCls} min-h-[90px]`}
                    value={s.narration}
                    placeholder="What is said"
                    onChange={(e) => setScene(i, { narration: e.target.value })}
                  />
                  <div className="flex gap-1.5">
                    <button className="text-[11.5px] font-semibold text-[#1b2d5e]" onClick={() => move(i, -1)}><ArrowUp className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}Up</button>
                    <button className="text-[11.5px] font-semibold text-[#1b2d5e]" onClick={() => move(i, 1)}><ArrowDown className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}Down</button>
                    <button
                      className="ml-auto text-[11.5px] font-semibold text-[#b94a3a]"
                      onClick={() => setScenes((x) => x.filter((_, j) => j !== i))}
                    >
                      Remove scene
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <>
                <p className="text-[12.5px] italic leading-5 text-[#5f6780]">{s.screen}</p>
                <p className="text-xs leading-6 text-[#16213e]">{s.narration}</p>
              </>
            )}
          </div>
        ))}
      </div>
      {editing && (
        <Button
          size="sm"
          variant="secondary"
          onClick={() => setScenes((s) => [...s, { screen: "", narration: "" }])}
        >
          + Add scene
        </Button>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   ACTIVITY & EVIDENCE
───────────────────────────────────────────────────────────────── */

const WORKSPACE_LABEL: Record<string, string> = {
  "": "None",
  scratch: "Scratch",
  web: "Code editor (HTML, CSS, JavaScript with a live preview)",
  react: "React app (code editor, Vite-style, runs in the browser)",
  native: "React Native app (code editor, phone preview)",
};

function ActivityPanel({ lesson }: { lesson: MicroLesson }) {
  const [editing, setEditing] = useState(false);
  const [msg, setMsg] = useState("");
  const [save, { isLoading }] = useUpdateMicroLessonMutation();
  const [del, { isLoading: deleting }] = useDeleteMicroLessonMutation();

  const initial = () => ({
    title: lesson.title,
    strand: lesson.strand,
    objective: lesson.objective ?? "",
    watchMinutes: lesson.watchMinutes,
    doMinutes: lesson.doMinutes,
    uploadMinutes: lesson.uploadMinutes,
    steps: lesson.action.steps.join("\n"),
    materials: lesson.action.materials.join("\n"),
    offScreen: lesson.action.offScreen,
    evidenceTypes: lesson.evidence.types,
    evidencePrompt: lesson.evidence.prompt ?? "",
    rubricFocus: lesson.rubricFocus,
    parentNote: lesson.parentNote ?? "",
    workspace: lesson.workspace ?? "",
    status: lesson.status,
  });
  const [f, setF] = useState(initial);
  useEffect(() => setF(initial()), [lesson]); // eslint-disable-line react-hooks/exhaustive-deps

  const total = Number(f.watchMinutes) + Number(f.doMinutes) + Number(f.uploadMinutes);
  const toggle = (list: string[], v: string) =>
    list.includes(v) ? list.filter((x) => x !== v) : [...list, v];
  const lines = (s: string) => s.split("\n").map((x) => x.trim()).filter(Boolean);

  const onSave = async () => {
    setMsg("");
    const body: LessonPatch = {
      title: f.title,
      strand: f.strand,
      objective: f.objective,
      watchMinutes: Number(f.watchMinutes),
      doMinutes: Number(f.doMinutes),
      uploadMinutes: Number(f.uploadMinutes),
      action: { steps: lines(f.steps), materials: lines(f.materials), offScreen: f.offScreen },
      evidence: { types: f.evidenceTypes, prompt: f.evidencePrompt },
      rubricFocus: f.rubricFocus,
      parentNote: f.parentNote,
      workspace: f.workspace in WORKSPACE_LABEL && f.workspace ? (f.workspace as MicroLesson["workspace"]) : null,
      status: f.status,
    };
    try {
      await save({ key: lesson.key, weekKey: lesson.weekKey, body }).unwrap();
      setEditing(false);
      setMsg("Saved");
    } catch (err) {
      setMsg(errorText(err, "Could not save"));
    }
  };

  const onDelete = async () => {
    if (!window.confirm(`Remove “${lesson.title}”? This cannot be undone.`)) return;
    try {
      await del({ key: lesson.key, weekKey: lesson.weekKey }).unwrap();
    } catch (err) {
      setMsg(errorText(err, "Could not remove"));
    }
  };

  if (!editing) {
    return (
      <div className="space-y-4 text-xs text-[#3b4560]">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <p className={labelCls}>After the video, the child…</p>
            <ol className="list-decimal space-y-1 pl-4 leading-5">
              {lesson.action.steps.map((s, i) => <li key={i}>{s}</li>)}
            </ol>
          </div>
          <div className="space-y-3">
            <div>
              <p className={labelCls}>Materials</p>
              <p>{lesson.action.materials.join(" · ") || "—"}</p>
            </div>
            <div>
              <p className={labelCls}>Evidence ({lesson.evidence.types.join(", ")})</p>
              <p className="leading-5">{lesson.evidence.prompt ?? "—"}</p>
            </div>
            <div>
              <p className={labelCls}>In-app workspace</p>
              <p>{WORKSPACE_LABEL[lesson.workspace ?? ""]}</p>
            </div>
            <div>
              <p className={labelCls}>Rubric focus</p>
              <p>
                {lesson.rubricFocus
                  .map((r) => RUBRIC_AREAS.find((a) => a.value === r)?.label ?? r)
                  .join(" · ") || "—"}
              </p>
            </div>
          </div>
        </div>
        {lesson.parentNote && (
          <div className="rounded-[14px] border border-[#e5e8ef] bg-[#f9fafc] px-4 py-3">
            <p className={labelCls}>Note for the parent</p>
            <p className="leading-5">{lesson.parentNote}</p>
          </div>
        )}
        <div className="flex items-center justify-between">
          {msg && <p className="text-[12.5px] font-semibold text-[#5a6278]">{msg}</p>}
          <div className="ml-auto flex gap-2">
            {!lesson.video && lesson.videoHistory.length === 0 && (
              <Button size="sm" variant="danger" onClick={onDelete} disabled={deleting}>
                Remove micro-lesson
              </Button>
            )}
            <Button size="sm" onClick={() => setEditing(true)}>Edit details</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className={labelCls}>Title</label>
          <input className={inputCls} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
        </div>
        <div>
          <label className={labelCls}>Strand</label>
          <select className={inputCls} value={f.strand} onChange={(e) => setF({ ...f, strand: e.target.value })}>
            {STRANDS.map((s) => <option key={s}>{s}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className={labelCls}>Objective</label>
        <input className={inputCls} value={f.objective} onChange={(e) => setF({ ...f, objective: e.target.value })} />
      </div>
      <div className="grid grid-cols-4 gap-3">
        {(["watchMinutes", "doMinutes", "uploadMinutes"] as const).map((k) => (
          <div key={k}>
            <label className={labelCls}>{k.replace("Minutes", "")} (min)</label>
            <input
              type="number"
              min={1}
              className={inputCls}
              value={f[k]}
              onChange={(e) => setF({ ...f, [k]: Number(e.target.value) })}
            />
          </div>
        ))}
        <div>
          <label className={labelCls}>Total</label>
          <p className={`py-2 text-xs font-bold ${total > 40 ? "text-[#b94a3a]" : "text-[#2a7a56]"}`}>
            {total} / 40 min
          </p>
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className={labelCls}>Activity steps (one per line)</label>
          <textarea className={`${inputCls} min-h-[120px]`} value={f.steps} onChange={(e) => setF({ ...f, steps: e.target.value })} />
        </div>
        <div>
          <label className={labelCls}>Materials (one per line)</label>
          <textarea className={`${inputCls} min-h-[120px]`} value={f.materials} onChange={(e) => setF({ ...f, materials: e.target.value })} />
          <label className="mt-2 flex items-center gap-2 text-xs text-[#3b4560]">
            <input type="checkbox" checked={f.offScreen} onChange={(e) => setF({ ...f, offScreen: e.target.checked })} />
            The activity is mostly off-screen
          </label>
        </div>
      </div>
      <div>
        <label className={labelCls}>Evidence the child uploads</label>
        <div className="mb-2 flex flex-wrap gap-1.5">
          {EVIDENCE_TYPES.map((t) => (
            <button
              key={t}
              onClick={() => setF({ ...f, evidenceTypes: toggle(f.evidenceTypes, t) })}
              className={`rounded-full px-2.5 py-1 text-[11.5px] font-bold ${f.evidenceTypes.includes(t) ? "bg-[#1b2d5e] text-white" : "bg-[#f1f3f8] text-[#8189a0]"}`}
            >
              {t}
            </button>
          ))}
        </div>
        <input className={inputCls} value={f.evidencePrompt} placeholder="What exactly should they upload?" onChange={(e) => setF({ ...f, evidencePrompt: e.target.value })} />
      </div>
      <div>
        <label className={labelCls}>In-app workspace (on the child's Do step)</label>
        <select className={inputCls} value={f.workspace} onChange={(e) => setF({ ...f, workspace: e.target.value })}>
          {Object.entries(WORKSPACE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <p className="mt-1 text-[11.5px] leading-4 text-[#8189a0]">
          The child can do the activity inside the app and hand the project in. They can still work elsewhere and upload as usual.
        </p>
      </div>
      <div>
        <label className={labelCls}>Rubric focus</label>
        <div className="flex flex-wrap gap-1.5">
          {RUBRIC_AREAS.map((a) => (
            <button
              key={a.value}
              onClick={() => setF({ ...f, rubricFocus: toggle(f.rubricFocus, a.value) })}
              className={`rounded-full px-2.5 py-1 text-[11.5px] font-bold ${f.rubricFocus.includes(a.value) ? "bg-[#1b2d5e] text-white" : "bg-[#f1f3f8] text-[#8189a0]"}`}
            >
              {a.label}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className={labelCls}>Note for the parent</label>
        <textarea className={`${inputCls} min-h-[60px]`} value={f.parentNote} onChange={(e) => setF({ ...f, parentNote: e.target.value })} />
      </div>
      <div className="flex items-center justify-between gap-3">
        <label className="flex items-center gap-2 text-xs text-[#3b4560]">
          <input
            type="checkbox"
            checked={f.status === "approved"}
            onChange={(e) => setF({ ...f, status: e.target.checked ? "approved" : "draft" })}
          />
          Approved for children
        </label>
        <div className="flex gap-2">
          {msg && <p className="self-center text-[12.5px] font-semibold text-[#b94a3a]">{msg}</p>}
          <Button size="sm" variant="secondary" onClick={() => { setF(initial()); setEditing(false); }}>Cancel</Button>
          <Button size="sm" onClick={onSave} disabled={isLoading || total > 40}>
            {isLoading ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   VIDEO
───────────────────────────────────────────────────────────────── */

function VideoPlayer({ lessonKey, video }: { lessonKey: string; video: LessonVideo }) {
  const [url, setUrl] = useState<string | null>(null);
  const [getUrl, { isLoading, error }] = useGetVideoUrlMutation();

  const load = async () => {
    try {
      const res = await getUrl({ key: lessonKey, videoId: video.id }).unwrap();
      setUrl(res.data.url);
    } catch {
      /* error shown below */
    }
  };

  // A fixed 16:9 frame: the player fills it, so the box never collapses
  // while the video loads (same fix as the children's Watch step).
  return (
    <div className="relative aspect-video w-full overflow-hidden rounded-[14px] bg-[#16213e]">
      {url ? (
        <MediaPlayer key={url} src={url} autoPlay speed={0.85} className="h-full w-full rounded-none" />
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-white">
          <button
            onClick={load}
            disabled={isLoading}
            aria-label="Play the video"
            className="flex h-14 w-14 items-center justify-center rounded-full bg-white/15 text-2xl hover:bg-white/25"
          >
            {isLoading ? <Spinner size="sm" /> : <Play className="h-4 w-4" aria-hidden />}
          </button>
          {error && <p className="text-[12.5px] text-[#fca5a5]">{errorText(error, "Could not load the video")}</p>}
        </div>
      )}
    </div>
  );
}

function VideoMeta({ v }: { v: LessonVideo }) {
  const sourceLabel = { generated: "Generated", recorded: "Recorded by a tutor", uploaded: "Uploaded" }[v.source];
  return (
    <p className="text-[12.5px] leading-5 text-[#5f6780]">
      <b className="text-[#3b4560]">{v.fileName ?? "Video"}</b> · {fmtDuration(v.durationSec)} · {fmtSize(v.sizeBytes)} · {sourceLabel}
      <br />
      {v.replacedAt ? `Replaced ${fmtWhen(v.replacedAt)}` : `Added ${fmtWhen(v.uploadedAt)}`}
      {v.uploadedByName ? ` · by ${v.uploadedByName}` : ""}
    </p>
  );
}

function VideoPanel({ lesson, storageConfigured }: { lesson: MicroLesson; storageConfigured: boolean }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [source, setSource] = useState<"recorded" | "generated" | "uploaded">("recorded");
  const [progress, setProgress] = useState<number | null>(null);
  const [msg, setMsg] = useState("");
  const [getUploadUrl] = useGetVideoUploadUrlMutation();
  const [attach] = useAttachVideoMutation();
  const [restore, { isLoading: restoring }] = useRestoreVideoMutation();
  const [remove, { isLoading: removing }] = useRemoveVideoMutation();

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setMsg("");
    if (!file.type.startsWith("video/")) {
      setMsg("Choose a video file (MP4 is best).");
      return;
    }
    const duration = await readDuration(file);
    if (duration != null && duration > 30 * 60) {
      setMsg(`This video is ${Math.round(duration / 60)} minutes long. Lesson videos must be 30 minutes or less.`);
      return;
    }
    if (duration != null && duration > lesson.watchMinutes * 60 * 1.5) {
      const ok = window.confirm(
        `This video is ${fmtDuration(duration)} but the lesson's watch time is ${lesson.watchMinutes} minutes. Upload anyway?`,
      );
      if (!ok) return;
    }
    try {
      setProgress(0);
      const { data } = await getUploadUrl({
        key: lesson.key,
        fileName: file.name,
        contentType: file.type,
        sizeBytes: file.size,
      }).unwrap();
      await putWithProgress(data.uploadUrl, file, setProgress);
      await attach({
        key: lesson.key,
        weekKey: lesson.weekKey,
        body: {
          key: data.key,
          durationSec: duration,
          fileName: file.name,
          contentType: file.type,
          source,
        },
      }).unwrap();
      setMsg(lesson.video ? "New video saved. The old one is in the history below." : "Video saved.");
    } catch (err) {
      setMsg(errorText(err, "Upload failed"));
    } finally {
      setProgress(null);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onRemove = async () => {
    if (!window.confirm("Take this video down? Children will no longer see it. It stays in the history so you can restore it.")) return;
    try {
      await remove({ key: lesson.key, weekKey: lesson.weekKey }).unwrap();
    } catch (err) {
      setMsg(errorText(err, "Could not take the video down"));
    }
  };

  const onRestore = async (videoId: string) => {
    try {
      await restore({ key: lesson.key, weekKey: lesson.weekKey, videoId }).unwrap();
      setMsg("Earlier video restored.");
    } catch (err) {
      setMsg(errorText(err, "Could not restore"));
    }
  };

  return (
    <div className="space-y-4">
      {!storageConfigured && (
        <p className="rounded-[14px] border border-[#f5d9a0] bg-[#fff8ee] px-4 py-3 text-[12.5px] text-[#7a4a10]">
          Video storage is not set up on the server yet, so uploads and playback are switched off. The script is ready to record from.
        </p>
      )}

      {lesson.video ? (
        <div className="grid gap-4 sm:grid-cols-[1.4fr_1fr]">
          {storageConfigured ? (
            <VideoPlayer key={lesson.video.id} lessonKey={lesson.key} video={lesson.video} />
          ) : (
            <div className="flex aspect-video items-center justify-center rounded-[14px] bg-[#16213e] text-xs text-white/60">Video attached</div>
          )}
          <div className="space-y-3">
            <VideoMeta v={lesson.video} />
            <Button size="sm" variant="danger" onClick={onRemove} disabled={removing}>Take video down</Button>
          </div>
        </div>
      ) : (
        <p className="rounded-[14px] border border-dashed border-[#cdd4e2] px-4 py-6 text-center text-xs text-[#8189a0]">
          No video yet. Children will see the script as text until one is added.
        </p>
      )}

      <div className="rounded-[14px] border border-[#e5e8ef] bg-[#f9fafc] p-4">
        <p className={labelCls}>{lesson.video ? "Replace the video" : "Add a video"}</p>
        <div className="flex flex-wrap items-center gap-2">
          <select
            className="rounded-xl border border-[#dde2ec] bg-white px-3 py-2 text-xs text-[#16213e] outline-none focus:border-[#1b2d5e]"
            value={source}
            onChange={(e) => setSource(e.target.value as typeof source)}
          >
            <option value="recorded">Recorded by a tutor</option>
            <option value="generated">Generated</option>
            <option value="uploaded">Other upload</option>
          </select>
          <input
            ref={fileRef}
            type="file"
            accept="video/*"
            className="hidden"
            onChange={(e) => onFile(e.target.files?.[0])}
          />
          <Button
            size="sm"
            onClick={() => fileRef.current?.click()}
            disabled={!storageConfigured || progress !== null}
          >
            {progress !== null ? `Uploading ${progress}%` : "Choose video file"}
          </Button>
          <p className="text-[11.5px] text-[#8189a0]">MP4, 30 minutes or less. The current video moves to history.</p>
        </div>
        {progress !== null && (
          <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-[#e5e8ef]">
            <div className="h-full bg-[#1b2d5e] transition-all" style={{ width: `${progress}%` }} />
          </div>
        )}
        {msg && <p className="mt-2 text-[12.5px] font-semibold text-[#5a6278]">{msg}</p>}
      </div>

      {lesson.videoHistory.length > 0 && (
        <div>
          <p className={labelCls}>Earlier videos ({lesson.videoHistory.length})</p>
          <div className="space-y-2">
            {lesson.videoHistory.map((v) => (
              <HistoryRow key={v.id} lessonKey={lesson.key} v={v} storageConfigured={storageConfigured} onRestore={() => onRestore(v.id)} restoring={restoring} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function HistoryRow({
  lessonKey, v, storageConfigured, onRestore, restoring,
}: {
  lessonKey: string;
  v: LessonVideo;
  storageConfigured: boolean;
  onRestore: () => void;
  restoring: boolean;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-[12px] border border-[#e5e8ef] bg-white px-3 py-2">
      <div className="flex items-center justify-between gap-3">
        <VideoMeta v={v} />
        <div className="flex flex-shrink-0 gap-1.5">
          {storageConfigured && (
            <Button size="sm" variant="ghost" onClick={() => setOpen((o) => !o)}>{open ? "Hide" : "Play"}</Button>
          )}
          <Button size="sm" variant="secondary" onClick={onRestore} disabled={restoring}>Restore</Button>
        </div>
      </div>
      {open && <div className="mt-2 max-w-md"><VideoPlayer lessonKey={lessonKey} video={v} /></div>}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   MICRO-LESSON CARD
───────────────────────────────────────────────────────────────── */

function LessonCard({ lesson, week }: { lesson: MicroLesson; week: WeekDetail }) {
  const [open, setOpen] = useState(lesson.order === 1);
  const [tab, setTab] = useState<"script" | "activity" | "video">("script");

  return (
    <div className="overflow-hidden rounded-[18px] border border-[#e5e8ef] bg-white">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-3 px-5 py-4 text-left transition hover:bg-[#f9fafc]"
      >
        <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-[#ebeff7] text-xs font-bold text-[#1b2d5e]">
          {lesson.order}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-bold text-[#16213e]">{lesson.title}</p>
          <p className="mt-0.5 text-[12.5px] text-[#8189a0]">
            {lesson.strand} · watch {lesson.watchMinutes} · do {lesson.doMinutes} · upload {lesson.uploadMinutes} ={" "}
            <b className="text-[#3b4560]">{lesson.totalMinutes} min</b>
          </p>
        </div>
        <span className={`hidden rounded-full px-2 py-0.5 text-[11px] font-bold sm:inline ${lesson.video ? "bg-[#edfaf4] text-[#2a7a56]" : "bg-[#f1f3f8] text-[#8189a0]"}`}>
          {lesson.video ? <><Play className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden /> {fmtDuration(lesson.video.durationSec)}</> : "No video"}
        </span>
        <span className={`hidden rounded-full px-2 py-0.5 text-[11px] font-bold sm:inline ${lesson.status === "approved" ? "bg-[#edfaf4] text-[#2a7a56]" : "bg-[#fff8ee] text-[#7a4a10]"}`}>
          {lesson.status === "approved" ? "Approved" : "Draft"}
        </span>
      </button>

      {open && (
        <div className="border-t border-[#ebeff7] px-5 pb-5 pt-4">
          {lesson.objective && (
            <p className="mb-4 text-xs leading-5 text-[#3b4560]">
              <b>Objective:</b> {lesson.objective}
            </p>
          )}
          <div className="mb-4 flex gap-1">
            {(["script", "activity", "video"] as const).map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`rounded-xl px-3 py-1.5 text-[12.5px] font-bold transition ${tab === t ? "bg-[#1b2d5e] text-white" : "bg-[#f1f3f8] text-[#8189a0] hover:text-[#16213e]"}`}
              >
                {t === "script" ? "Script" : t === "activity" ? "Activity & evidence" : `Video${lesson.videoHistory.length ? ` (${lesson.videoHistory.length} earlier)` : ""}`}
              </button>
            ))}
          </div>
          {tab === "script" && <ScriptPanel lesson={lesson} week={week} />}
          {tab === "activity" && <ActivityPanel lesson={lesson} />}
          {tab === "video" && <VideoPanel lesson={lesson} storageConfigured={week.storageConfigured} />}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   WEEKLY TARGET
───────────────────────────────────────────────────────────────── */

function TargetPanel({ week }: { week: WeekDetail }) {
  const [editing, setEditing] = useState(false);
  const [msg, setMsg] = useState("");
  const [save, { isLoading }] = useUpdateLibraryWeekMutation();
  const initial = () => ({
    must: week.weeklyTarget.must.join("\n"),
    stretch: week.weeklyTarget.stretch.join("\n"),
    parentBrief: week.parentBrief ?? "",
    homeMaterials: week.homeMaterials.join("\n"),
    hybridOnsite: week.hybridOnsite,
  });
  const [f, setF] = useState(initial);
  useEffect(() => setF(initial()), [week]); // eslint-disable-line react-hooks/exhaustive-deps
  const lines = (s: string) => s.split("\n").map((x) => x.trim()).filter(Boolean);

  const onSave = async () => {
    setMsg("");
    try {
      await save({
        key: week.key,
        body: {
          weeklyTarget: { must: lines(f.must), stretch: lines(f.stretch) },
          parentBrief: f.parentBrief,
          homeMaterials: lines(f.homeMaterials),
          hybridOnsite: f.hybridOnsite,
        },
      }).unwrap();
      setEditing(false);
      setMsg("Saved");
    } catch (err) {
      setMsg(errorText(err, "Could not save"));
    }
  };

  if (!editing)
    return (
      <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="rounded-[16px] border border-[#cdd4e2] bg-[#f1f3f8] p-4">
            <p className={labelCls}>Must do this week</p>
            <ul className="space-y-1.5 text-xs leading-5 text-[#16213e]">
              {week.weeklyTarget.must.map((m, i) => <li key={i}><Square className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}{m}</li>)}
            </ul>
            {week.weeklyTarget.source === "lesson_plan" && (
              <p className="mt-3 text-[11.5px] text-[#8189a0]">Taken from the lesson plan's deliverable. Edit it to make it your own.</p>
            )}
          </div>
          <div className="rounded-[16px] border border-[#e5e8ef] bg-white p-4">
            <p className={labelCls}>Stretch</p>
            {week.weeklyTarget.stretch.length ? (
              <ul className="space-y-1.5 text-xs leading-5 text-[#3b4560]">
                {week.weeklyTarget.stretch.map((m, i) => <li key={i}><Star className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}{m}</li>)}
              </ul>
            ) : (
              <p className="text-xs text-[#a6adbf]">None yet.</p>
            )}
          </div>
        </div>
        <div className="rounded-[16px] border border-[#e5e8ef] bg-white p-4">
          <p className={labelCls}>Parent brief</p>
          <p className="text-xs leading-6 text-[#3b4560]">{week.parentBrief ?? "Not written yet."}</p>
          {week.homeMaterials.length > 0 && (
            <>
              <p className={`${labelCls} mt-3`}>Home materials</p>
              <ul className="list-disc pl-4 text-xs leading-5 text-[#3b4560]">
                {week.homeMaterials.map((m, i) => <li key={i}>{m}</li>)}
              </ul>
            </>
          )}
        </div>
        <div className="flex items-center justify-between">
          <p className="text-[12.5px] text-[#8189a0]">
            Hybrid: {week.hybridOnsite ? "this week's activity happens on-site" : "fully online"}
          </p>
          <div className="flex items-center gap-2">
            {msg && <span className="text-[12.5px] font-semibold text-[#5a6278]">{msg}</span>}
            <Button size="sm" onClick={() => setEditing(true)}>Edit</Button>
          </div>
        </div>
      </div>
    );

  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className={labelCls}>Must do this week (one per line)</label>
          <textarea className={`${inputCls} min-h-[140px]`} value={f.must} onChange={(e) => setF({ ...f, must: e.target.value })} />
        </div>
        <div>
          <label className={labelCls}>Stretch (one per line)</label>
          <textarea className={`${inputCls} min-h-[140px]`} value={f.stretch} onChange={(e) => setF({ ...f, stretch: e.target.value })} />
        </div>
      </div>
      <div>
        <label className={labelCls}>Parent brief</label>
        <textarea className={`${inputCls} min-h-[110px]`} value={f.parentBrief} onChange={(e) => setF({ ...f, parentBrief: e.target.value })} />
      </div>
      <div>
        <label className={labelCls}>Home materials (one per line)</label>
        <textarea className={`${inputCls} min-h-[90px]`} value={f.homeMaterials} onChange={(e) => setF({ ...f, homeMaterials: e.target.value })} />
      </div>
      <label className="flex items-center gap-2 text-xs text-[#3b4560]">
        <input type="checkbox" checked={f.hybridOnsite} onChange={(e) => setF({ ...f, hybridOnsite: e.target.checked })} />
        In hybrid, this week's activity happens on-site
      </label>
      <div className="flex justify-end gap-2">
        {msg && <span className="self-center text-[12.5px] font-semibold text-[#b94a3a]">{msg}</span>}
        <Button size="sm" variant="secondary" onClick={() => { setF(initial()); setEditing(false); }}>Cancel</Button>
        <Button size="sm" onClick={onSave} disabled={isLoading}>{isLoading ? "Saving…" : "Save"}</Button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   SHOWCASE CRITERIA (showcase weeks only)
───────────────────────────────────────────────────────────────── */

function CriteriaPanel({ week }: { week: WeekDetail }) {
  const [editing, setEditing] = useState(false);
  const [msg, setMsg] = useState("");
  const [save, { isLoading }] = useUpdateLibraryWeekMutation();
  const current = week.showcaseCriteria ?? [];
  const [text, setText] = useState(current.join("\n"));
  useEffect(() => setText((week.showcaseCriteria ?? []).join("\n")), [week]);

  const send = async (showcaseCriteria: string[] | null) => {
    setMsg("");
    try {
      await save({ key: week.key, body: { showcaseCriteria } }).unwrap();
      setEditing(false);
      setMsg("Saved");
    } catch (err) {
      setMsg(errorText(err, "Could not save"));
    }
  };

  return (
    <div className="mt-4 rounded-[16px] border border-[#e3c76a] bg-[#fbf5e1] p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className={labelCls}>Showcase panel criteria</p>
          <p className="text-[12.5px] leading-5 text-[#5f6780]">
            The panel scores each one from 1 to 5. Changes apply to showcases nobody has scored yet; scored ones keep
            their criteria.
          </p>
        </div>
        {!editing && (
          <div className="flex flex-shrink-0 items-center gap-2">
            {msg && <span className="text-[12.5px] font-semibold text-[#5a6278]">{msg}</span>}
            <Button size="sm" onClick={() => setEditing(true)}>Edit</Button>
          </div>
        )}
      </div>
      {editing ? (
        <div className="mt-3 space-y-2">
          <label className={labelCls} htmlFor="criteria">Criteria (one per line, 2 to 8)</label>
          <textarea id="criteria" className={`${inputCls} min-h-[150px]`} value={text} onChange={(e) => setText(e.target.value)} />
          <div className="flex flex-wrap justify-end gap-2">
            {msg && <span className="self-center text-[12.5px] font-semibold text-[#b94a3a]">{msg}</span>}
            {week.showcaseCriteriaCustom && (
              <Button size="sm" variant="secondary" onClick={() => send(null)} disabled={isLoading}>Use the defaults</Button>
            )}
            <Button size="sm" variant="secondary" onClick={() => { setText(current.join("\n")); setEditing(false); }}>Cancel</Button>
            <Button
              size="sm"
              onClick={() => send(text.split("\n").map((x) => x.trim()).filter(Boolean))}
              disabled={isLoading}
            >
              {isLoading ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      ) : (
        <>
          <ol className="mt-3 list-decimal space-y-1 pl-5 text-xs leading-5 text-[#16213e]">
            {current.map((c, i) => <li key={i}>{c}</li>)}
          </ol>
          <p className="mt-2 text-[11.5px] text-[#8189a0]">
            {week.showcaseCriteriaCustom ? "Your own criteria." : "The default criteria, from what the lesson tells the child the panel looks at."}
          </p>
        </>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   FULL LESSON PLAN
───────────────────────────────────────────────────────────────── */

const SESSION_LABELS: [string, string][] = [
  ["duration", "Classroom duration"],
  ["strands", "Strands"],
  ["buildsOn", "Builds on"],
  ["feedsInto", "Feeds into"],
  ["materials", "Materials"],
  ["preWork", "Pre-work"],
  ["deliverable", "Deliverable"],
];

function LessonPlanPanel({ week }: { week: WeekDetail }) {
  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2">
        {SESSION_LABELS.filter(([k]) => week.session[k]).map(([k, label]) => (
          <div key={k} className="rounded-[14px] border border-[#e5e8ef] bg-[#f9fafc] px-4 py-3">
            <p className={labelCls}>{label}</p>
            <p className="text-xs leading-5 text-[#16213e]">{week.session[k]}</p>
          </div>
        ))}
      </div>
      {week.sessionMap.length > 0 && (
        <div className="rounded-[14px] border border-[#e5e8ef] bg-white px-4 py-3">
          <p className={labelCls}>Classroom session map</p>
          <ul className="space-y-1 font-mono text-[12.5px] text-[#3b4560]">
            {week.sessionMap.map((s, i) => <li key={i}>{s}</li>)}
          </ul>
        </div>
      )}
      {week.sourceFile ? (
        <>
          <p className="text-[11.5px] text-[#8189a0]">
            Source: {week.sourceFile} · {week.lessonPlanWords.toLocaleString()} words
          </p>
          <div
            className="lesson-plan max-h-[75vh] overflow-y-auto rounded-[16px] border border-[#e5e8ef] bg-white px-6 py-5"
            dangerouslySetInnerHTML={{ __html: week.lessonPlanHtml ?? "<p>No lesson plan.</p>" }}
          />
        </>
      ) : (
        <p className="rounded-[14px] border border-dashed border-[#e5e8ef] bg-white px-4 py-3 text-xs text-[#6b6880]">
          The source curriculum has no lesson plan for this week. It was written by Future Smart Lab, so the
          micro-lessons and their scripts are the whole plan.
        </p>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   PAGE
═══════════════════════════════════════════════════════════════ */

export default function AdminLessonWeek() {
  const { weekKey = "" } = useParams();
  const { data, isLoading, error } = useGetLibraryWeekQuery(weekKey);
  const [addLesson, { isLoading: adding }] = useAddMicroLessonMutation();
  const [updateWeek] = useUpdateLibraryWeekMutation();
  const [tab, setTab] = useState<"lessons" | "target" | "plan">("lessons");
  const week = data?.data;

  if (isLoading)
    return (
      <div className="space-y-3">
        <Skeleton className="h-20 rounded-[18px]" />
        <Skeleton className="h-64 rounded-[18px]" />
      </div>
    );
  if (error || !week)
    return <EmptyState icon={FileText} title="Week not found" action={<Link to="/admin/curriculum" className="text-sm font-semibold text-[#1b2d5e]"><ArrowLeft className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}Back to the library</Link>} />;

  const tc = trackConfig(week.track);
  const conv = CONVERSION_LABEL[week.conversionStatus];
  const total = week.microLessons.reduce((n, l) => n + l.totalMinutes, 0);

  return (
    <>
      <Link to="/admin/curriculum" className="mb-3 inline-block text-xs font-semibold text-[#1b2d5e]">
       {" "}<ArrowLeft className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}Lesson library
      </Link>

      <div className="mb-5 rounded-[22px] border border-[#e5e8ef] bg-white p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[11.5px] font-bold uppercase tracking-wider" style={{ color: tc.text }}>
              {tc.label} · {week.term} · Week {week.weekNumber}
              {week.isShowcase && <> · <Star className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden /> Showcase</>}
            </p>
            <h1 className="mt-1 text-[1.4rem] font-bold tracking-[-0.03em] text-[#16213e]">{week.title}</h1>
            {week.topics && <p className="mt-1 text-xs text-[#5f6780]">{week.topics}</p>}
          </div>
          <div className="flex flex-col items-end gap-2">
            <select
              value={week.conversionStatus}
              title="Approving the week approves all its micro-lessons, so children can see them"
              onChange={(e) => {
                const v = e.target.value;
                if (v === "approved" && !window.confirm("Approve this week? All its micro-lessons become visible to children.")) return;
                updateWeek({ key: week.key, body: { conversionStatus: v } });
              }}
              className={`rounded-full border-0 px-3 py-1 text-[11.5px] font-bold outline-none ${conv.cls}`}
            >
              {Object.entries(CONVERSION_LABEL).map(([v, c]) => (
                <option key={v} value={v}>{c.label}</option>
              ))}
            </select>
            {week.microLessons.length > 0 && (
              <p className="text-[12.5px] text-[#8189a0]">
                {week.microLessons.length} micro-lessons · {total} min this week
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-1">
        {([
          ["lessons", `Micro-lessons (${week.microLessons.length})`],
          ["target", "Weekly target & parent brief"],
          ["plan", "Full lesson plan"],
        ] as const).map(([t, label]) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`rounded-xl px-3.5 py-2 text-xs font-bold transition ${tab === t ? "bg-[#1b2d5e] text-white" : "bg-white border border-[#e5e8ef] text-[#8189a0] hover:text-[#16213e]"}`}
          >
            {label}
          </button>
        ))}
        {tab === "lessons" && week.microLessons.length > 0 && (
          <Button size="sm" variant="secondary" className="ml-auto" onClick={() => printScript(week, week.microLessons)}>
            Print the week's scripts
          </Button>
        )}
      </div>

      {tab === "lessons" && (
        <div className="space-y-3">
          {week.microLessons.length === 0 ? (
            <EmptyState
              icon={Clapperboard}
              title="Not turned into micro-lessons yet"
              subtitle="This week still has only its classroom lesson plan. Read it under “Full lesson plan”, then add micro-lessons here."
              action={
                <Button onClick={() => addLesson({ weekKey: week.key })} disabled={adding}>
                  + Add the first micro-lesson
                </Button>
              }
            />
          ) : (
            <>
              {week.microLessons.map((l) => (
                <LessonCard key={l.key} lesson={l} week={week} />
              ))}
              <Button variant="secondary" onClick={() => addLesson({ weekKey: week.key })} disabled={adding}>
                + Add micro-lesson
              </Button>
            </>
          )}
        </div>
      )}
      {tab === "target" && (
        <>
          <TargetPanel week={week} />
          {week.isShowcase && <CriteriaPanel week={week} />}
        </>
      )}
      {tab === "plan" && <LessonPlanPanel week={week} />}
    </>
  );
}
